/**
 * Which leg she is injecting, and which way that makes the guide point.
 *
 * A thigh injection has a side in a way an abdomen does not. The abdomen is one
 * area and the outline is symmetrical, so nothing downstream had to know left
 * from right; a leg is one of two, and both the drawing she matches herself to
 * and the band drawn on her body are wrong on the other one.
 *
 * Asked rather than detected. The pose model will happily report both knees and
 * a confidence for each, and picking the more confident one was what this did
 * first — which works right up to the moment she has both legs in frame, where
 * it silently outlines the wrong thigh and the only sign is a band on a leg she
 * was not about to inject. Her answer cannot be wrong about which leg she means.
 */
export type ThighSide = "left" | "right";

/**
 * Which way the knee points on screen, for the leg she named.
 *
 * ### The reasoning, so it can be checked rather than believed
 *
 * The silhouette is a profile: hip at one edge, thigh level, knee at the other.
 * To be seen in profile the phone has to be beside her, and it goes on the side
 * of the leg she is injecting — that is the leg she needs unoccluded, and the
 * other one would otherwise be in front of it.
 *
 * Stand to someone's right and face them: you are looking west, so your right
 * hand points north, which is the way they are facing. Their knees therefore
 * point to the **right** of the raw image. The preview is a front camera and is
 * mirrored, which puts them on the **left** of the screen. The left leg is the
 * same argument reflected.
 *
 * So: right leg → knee points left, left leg → knee points right. If this turns
 * out to be backwards in front of a real camera it is one line, and the
 * silhouette is the only thing that reads it — the band on her body comes from
 * `thighZoneFrom`, which mirrors her actual landmarks and does not consult this.
 */
export function kneePointsTo(side: ThighSide): "left" | "right" {
  return side === "right" ? "left" : "right";
}
